import { createMutation } from '@magicdoorinc/ui';
import { createResource, createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { invalidateVendorsCache, VendorRepository } from '~/repositories/vendorRepository';
import type { VendorFilter } from '~/repositories/vendorRepository';
import type { CreateVendorRequestDto, UpdateVendorRequestDto, VendorOverviewDto } from '~/swagger/maintenance/Api';

const repo = new VendorRepository();

export const [VendorsProvider, useVendors] = createMagicDoorContext('Vendors', () => {
  const [filter, setFilter] = createSignal<VendorFilter | undefined>(undefined);
  const [filtered, filteredActions] = createResource(filter, async (filter) => {
    try {
      return await repo.getVendors(filter);
    } catch {
      return { currentPage: 1, totalPages: 0, pageSize: filter.pageSize ?? 0, totalCount: 0, items: [], customFields: [] };
    }
  });

  const [currentId, setCurrentId] = createSignal<string | undefined>(undefined);
  const [current, currentActions] = createResource(currentId, (id) => repo.getVendor(id));
  const [vendorOverview, setVendorOverview] = createSignal<VendorOverviewDto | undefined>(undefined);

  const getVendorOverview = createMutation(async (vendorId: string) => {
    const overview = await repo.getVendorOverview(vendorId, true);
    setVendorOverview(overview);
    return overview;
  });

  const addVendor = createMutation(async (model: CreateVendorRequestDto) => {
    const created = await repo.createVendor(model);
    invalidateVendorsCache();
    currentActions.mutate(created);
    filteredActions.refetch();
    return created;
  });

  const updateVendor = createMutation(async (vendorId: string, model: UpdateVendorRequestDto) => {
    const updated = await repo.updateVendor(vendorId, model);
    invalidateVendorsCache();
    if (vendorId === currentId()) currentActions.mutate(updated);
    currentActions.refetch();
    return updated;
  });

  const deleteVendor = createMutation(async (vendorId: string) => {
    await repo.deleteVendor(vendorId);
    invalidateVendorsCache();
    if (vendorId === currentId()) currentActions.mutate(undefined);
    filteredActions.refetch();
  });

  const inviteVendor = createMutation(async (vendorId: string) => {
    const invite = await repo.portalInvite(vendorId);
    currentActions.refetch();
    return invite;
  });

  const changeVendorPassword = createMutation(async (vendorId: string, password: string) => {
    await repo.changeVendorPassword(vendorId, password);
    currentActions.refetch();
  });

  const deactivateVendor = createMutation(async (vendorId: string) => {
    const updated = await repo.deactivateVendor(vendorId);
    if (vendorId === currentId()) currentActions.mutate(updated);
    currentActions.refetch();
    filteredActions.refetch();
    return updated;
  });

  const reactivateVendor = createMutation(async (vendorId: string) => {
    const updated = await repo.reactivateVendor(vendorId);
    if (vendorId === currentId()) currentActions.mutate(updated);
    currentActions.refetch();
    filteredActions.refetch();
    return updated;
  });

  return {
    filtered,
    setFilter: (filter: VendorFilter) => setFilter(filter),
    current,
    setCurrentId,
    vendorOverview,
    changeVendorPassword,
    getVendorOverview,
    addVendor,
    updateVendor,
    deleteVendor,
    inviteVendor,
    deactivateVendor,
    reactivateVendor,
  };
});
